/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */
import { defineStore } from 'pinia';
import { WorkloadsApiService } from '@/services/api';
import WorkloadsHelper from '@/store/helpers/workloads.helper';
import WorkloadModel from '@/model/workloads/workload.model';
import ComposeWorkloadVersionModel from '@/model/workloads/compose-workload-version.model';
import Version from '@/model/workloads/workload-version.model';

const useDeployFormStore = defineStore('deployForm', {
  state: () => ({
    step: 1,
    formValid: false,
    deployName: '',
    isDisabled: false,
    origin: 'from-deployment',
    selectedWorkloadType: '',
    workloadsList: [],
    workloadsCount: 0,
    loadingWorkloads: false,
    versionsList: [],
    loadingVersions: false,
    selectedWorkload: {
      id: '',
      type: '',
      name: '',
      internalDockerRegistry: undefined,
    },
    selectedVersion: {
      id: '',
      name: '',
      numberOfServices: 0,
    },
    selectedNodesList: [],
  }),
  getters: {
    stepTwoWorkload: (state) => ({
      workloadId: state.selectedWorkload.id,
      versionId: state.selectedVersion.id,
    }),
  },
  actions: {
    resetAllDeployState() {
      this.step = 1;
      this.formValid = false;
      this.deployName = '';
      this.isDisabled = false;
      this.origin = 'from-deployment';
      this.selectedWorkloadType = '';
      this.workloadsList = [];
      this.workloadsCount = 0;
      this.loadingWorkloads = false;
      this.versionsList = [];
      this.loadingVersions = false;
      this.selectedWorkload = {
        id: '',
        type: '',
        name: '',
        internalDockerRegistry: undefined,
      };
      this.selectedVersion = {
        id: '',
        name: '',
        numberOfServices: 0,
      };
      this.selectedNodesList = [];
    },
    async fetchVersions() {
      const workloadId = this.selectedWorkload?.id;
      if (!workloadId) {
        this.versionsList = [];
        return;
      }

      this.loadingVersions = true;
      try {
        if (this.selectedWorkload.internalDockerRegistry || this.selectedWorkload.type === 'docker-compose') {
          const { data } = await WorkloadsApiService.getVersionListUsingV3Api(workloadId);
          this.versionsList = data
            .filter((v) => v?.isDeployable === true)
            .map((v) => {
              const m = new ComposeWorkloadVersionModel({ version: v });
              return { id: m._id, name: m.name, created: m.created, numberOfServices: m.numberOfServices };
            });
          return;
        }

        const wl = await WorkloadsApiService.getWlById({ id: workloadId });
        this.versionsList = (wl.versions || [])
          .filter((v) => v?.isDeployable === true)
          .map((v) => {
            const m = new Version({ version: v, type: this.selectedWorkload.type });
            return { id: m._id, name: m.name, created: m.created, numberOfServices: v.numberOfServices || 0 };
          });
      } finally {
        this.loadingVersions = false;
      }
    },
    async fetchWorkloads(parameters) {
      this.loadingWorkloads = true;
      try {
        const params = {
          filterBy: WorkloadsHelper.handleFilterBy({
            selectedType: parameters.type,
            search: parameters.search,
            disabled: false,
          }),
          limit: parameters.limit || 10,
          page: parameters.page || 1,
          order: WorkloadsHelper.handleOrder(parameters),
        };
        const workloads = await WorkloadsApiService.fetchWorkloads(params);
        this.workloadsList = workloads.data.map((w) => {
          const m = new WorkloadModel(w);
          return {
            id: m._id,
            name: m.name,
            description: m.description,
            created: m.created,
            internalDockerRegistry: m.internalDockerRegistry,
          };
        });
        this.workloadsCount = workloads.count;
      } finally {
        this.loadingWorkloads = false;
      }
    },
    setStep(step) {
      this.step = step;
    },
    setFormValid(formValid) {
      this.formValid = formValid;
    },
    setDeployName(deployName) {
      this.deployName = deployName;
    },
    setOrigin(origin) {
      this.origin = origin;
    },
    setDisabled(isDisabled) {
      this.isDisabled = isDisabled;
    },
    setSelectedWorkloadType(type) {
      this.selectedWorkloadType = type;
    },
    applyDeployInformation({ selectedWorkload = {}, selectedVersion = {} }) {
      Object.assign(this.selectedWorkload, {
        id: selectedWorkload.id || '',
        type: selectedWorkload.type || '',
        name: selectedWorkload.name || '',
        internalDockerRegistry: !!selectedWorkload.internalDockerRegistry,
      });

      Object.assign(this.selectedVersion, {
        id: selectedVersion.id || '',
        name: selectedVersion.name || '',
        numberOfServices: selectedVersion.numberOfServices || 0,
      });
    },
    clearDeploySelection() {
      this.versionsList = [];
      this.applyDeployInformation({
        selectedWorkload: {
          id: '',
          type: '',
          name: '',
          internalDockerRegistry: false,
        },
        selectedVersion: {
          id: '',
          name: '',
          numberOfServices: 0,
        },
      });
    },
    selectNodesForDeployment(nodes) {
      if (!nodes || nodes.length === 0) {
        this.selectedNodesList = [];
        return;
      }
      const newNodes = nodes.filter((n) => !this.selectedNodesList.some((s) => s.id === n.id));
      this.selectedNodesList.push(...newNodes);
    },
    removeSelectedNode(node) {
      const index = this.selectedNodesList.findIndex((n) => n.id === node.id);
      if (index !== -1) {
        this.selectedNodesList.splice(index, 1);
      }
    },
    deselectAllNodes() {
      this.selectedNodesList = [];
    },
    asRouteParamString(value) {
      if (Array.isArray(value)) {
        return value[0];
      }
      return value;
    },
    parseRouteBoolean(value) {
      const normalizedValue = this.asRouteParamString(value);

      if (normalizedValue === true || normalizedValue === 'true') {
        return true;
      }
      if (normalizedValue === false || normalizedValue === 'false') {
        return false;
      }
      return undefined;
    },
    getInMemoryDeployInformationForRoute({ workloadId, versionId, workloadType, usingRegistry }) {
      const selectedWorkload = this.selectedWorkload || {};
      const selectedVersion = this.selectedVersion || {};

      const isSameSelection =
        selectedWorkload.id === workloadId &&
        selectedVersion.id === versionId &&
        selectedWorkload.type === workloadType;
      const isSameRegistryFlag = !!selectedWorkload.internalDockerRegistry === usingRegistry;

      if (!isSameSelection || !isSameRegistryFlag) {
        return null;
      }

      return {
        selectedWorkload,
        selectedVersion,
      };
    },
    async hydrateDeployInformationFromRoute({ workloadId, versionId, workloadType, usingRegistry }) {
      if (!workloadId || !versionId || !workloadType) {
        throw new Error('Missing deploy route params.');
      }

      let workload;
      let shouldUseV3Routes = workloadType === 'docker-compose';

      if (workloadType === 'docker') {
        if (typeof usingRegistry !== 'boolean') {
          throw new Error('Missing usingRegistry route param.');
        }
        shouldUseV3Routes = usingRegistry;
      }

      if (shouldUseV3Routes) {
        const [v3Workload, { data: v3Versions }] = await Promise.all([
          WorkloadsApiService.getWorkloadUsingV3Api({ id: workloadId }),
          WorkloadsApiService.getVersionListUsingV3Api(workloadId),
        ]);

        const version = v3Versions?.find((v) => v._id === versionId);
        if (!version) {
          throw new Error('Version not found.');
        }

        return {
          selectedWorkload: {
            id: workloadId,
            type: workloadType,
            name: v3Workload?.name || '',
            internalDockerRegistry: v3Workload?.internalDockerRegistry,
          },
          selectedVersion: {
            id: versionId,
            name: version?.name || '',
            numberOfServices: version?.numberOfServices || 0,
          },
        };
      }

      if (!workload) {
        workload = await WorkloadsApiService.getWlById({ id: workloadId });
      }

      const version = workload?.versions?.find((v) => v._id === versionId);
      if (!version) {
        throw new Error('Version not found.');
      }

      return {
        selectedWorkload: {
          id: workloadId,
          type: workloadType,
          name: workload?.name || '',
          internalDockerRegistry: workload?.internalDockerRegistry,
        },
        selectedVersion: {
          id: versionId,
          name: version?.name || '',
          numberOfServices: version?.numberOfServices || 0,
        },
      };
    },
    async initializeFromRoute(route) {
      if (route.name !== 'Deploy Form Step Two' && route.name !== 'Deploy Form Step Three') {
        // If we already have a selection, user is navigating back from Step 2 to Step 1 - preserve state
        if (this.selectedWorkload.id) {
          this.setStep(1);
          return { shouldRedirectToStepOne: false };
        }
        this.resetAllDeployState();
        return { shouldRedirectToStepOne: false };
      }

      const workloadType = this.asRouteParamString(route.params.type);
      const workloadId = this.asRouteParamString(route.params.workloadId);
      const versionId = this.asRouteParamString(route.params.versionId);
      const routeOrigin = this.asRouteParamString(route.params.origin);
      const usingRegistry = this.parseRouteBoolean(route.params.usingRegistry);

      if (!workloadType || !workloadId || !versionId || !routeOrigin) {
        this.setStep(1);
        return { shouldRedirectToStepOne: true };
      }

      if (usingRegistry === undefined) {
        this.setStep(1);
        return { shouldRedirectToStepOne: true };
      }

      this.setOrigin(routeOrigin);
      this.setSelectedWorkloadType(workloadType);

      const inMemoryDeployInformation = this.getInMemoryDeployInformationForRoute({
        workloadId,
        versionId,
        workloadType,
        usingRegistry,
      });

      if (inMemoryDeployInformation) {
        // Hydrate versionsList from in-memory data for Step 2/3 refresh
        if (route.name === 'Deploy Form Step Two' || route.name === 'Deploy Form Step Three') {
          if (inMemoryDeployInformation.selectedVersion && !this.versionsList.length) {
            await this.fetchVersions();
          }
        }

        const targetStep = route.name === 'Deploy Form Step Three' ? 3 : 2;
        this.setStep(targetStep);
        return { shouldRedirectToStepOne: false };
      }

      try {
        const hydratedDeployInformation = await this.hydrateDeployInformationFromRoute({
          workloadId,
          versionId,
          workloadType,
          usingRegistry,
        });
        this.applyDeployInformation(hydratedDeployInformation);

        // Hydrate versionsList for Step 2/3 refresh
        if (route.name === 'Deploy Form Step Two' || route.name === 'Deploy Form Step Three') {
          await this.fetchVersions();
        }

        const targetStep = route.name === 'Deploy Form Step Three' ? 3 : 2;
        this.setStep(targetStep);
        return { shouldRedirectToStepOne: false };
      } catch {
        this.setStep(1);
        return { shouldRedirectToStepOne: true };
      }
    },
    buildStepTwoRouteParams() {
      return {
        type: this.selectedWorkload.type,
        usingRegistry: String(this.selectedWorkload.internalDockerRegistry === true),
        workloadId: this.selectedWorkload.id,
        versionId: this.selectedVersion.id,
        origin: this.origin,
      };
    },
    hasStepOneSelection() {
      return !!this.selectedWorkload.id && !!this.selectedVersion.id;
    },
  },
});

export default useDeployFormStore;
